// QP AI - hidden entry point on the Footprint world map.
//
// Keys are invisible spots on the fullscreen world map; clicking one 5 times in
// quick succession opens QP AI. Coordinates are lightly encoded (base64 "lat,lon")
// so they don't read as plain map data - this is hiding, not security: anything
// shipped to the browser can be found. Real access control for a different AI
// directive has to live on the backend.
//
// To add a key later: btoa("<lat>,<lon>") and append it here.
const ENCODED_KEYS = ["OS45Mjc4LDc4LjExOTg="]; // Madurai

export const QP_AI_KEYS = ENCODED_KEYS.map((k) => {
  const [lat, lon] = atob(k).split(",").map(Number);
  return { lat, lon };
});

export const CLICKS_TO_UNLOCK = 5;
// QP AI keys arm once the route colour sequence has played through
// (see RouteColorCycle.jsx). The Sangli speed key is live from the start.

// Sangli key: glow length after the 5th click, then the dash-speed multiplier
// reached by each further click (stepping up to 5x).
export const BOOST_GLOW_MS = 2000;

// How long "Welcome to QP AI" (title + voice) holds after the dots have gone,
// before the chat opens.
export const WELCOME_MS = 2800;
export const SPEED_STEPS = [1.8, 2.6, 3.4, 4.2, 5];
export const CLICK_WINDOW_MS = 1200; // max gap between consecutive clicks

// Palette borrowed from the "Signature Gradient" report styles.
export const QP_AI_COLORS = {
  light: ["#1A3C88", "#139DC2", "#51C2BA", "#87BD25"],
  dark: ["#8FB0FF", "#42E8DF", "#51C2BA", "#A0E134"]
};

// ---- Voice -----------------------------------------------------------------
// Browser speech synthesis (no server round trip, works offline). The voice is
// a woman's, as close to Siri as each device allows: Apple's own voices on
// iPhone/Mac, Microsoft's natural voices on Windows/Edge, Google's female
// English voice on Android/Chrome. First match in this list wins.
const PREFERRED_VOICES = [
  /^(Ava|Samantha|Allison|Susan|Serena|Kate|Karen|Moira|Tessa|Fiona)\b/i, // Apple (Siri-family)
  /Microsoft (Aria|Jenny|Sonia|Libby|Natasha|Clara|Emma|Ava).*Natural/i, // Windows/Edge natural
  /Google UK English Female/i,
  /Google US English/i, // Chrome's default US voice is female
  /Microsoft (Zira|Hazel|Susan|Catherine)/i, // older Windows voices
  /female/i
];

const pickVoice = () => {
  const voices = window.speechSynthesis?.getVoices?.() || [];
  for (const re of PREFERRED_VOICES) {
    const v = voices.find((x) => re.test(x.name) && /^en/i.test(x.lang));
    if (v) return v;
  }
  return voices.find((v) => /^en(-|_)GB/i.test(v.lang)) || voices.find((v) => /^en/i.test(v.lang)) || null;
};

// Chrome loads voices asynchronously - call this early (first key click) so a
// voice is ready by the time we speak.
export const warmUpVoices = () => {
  try {
    window.speechSynthesis?.getVoices();
  } catch {
    /* no speech support */
  }
};

export const speakWelcome = () => {
  try {
    const synth = window.speechSynthesis;
    if (!synth) return;
    synth.cancel();
    const u = new SpeechSynthesisUtterance("Welcome to Q.P. A.I.");
    const voice = pickVoice();
    if (voice) u.voice = voice;
    u.lang = voice?.lang || "en-GB";
    u.rate = 0.95;
    u.pitch = 1.05;
    u.volume = 1;
    synth.speak(u);
  } catch {
    /* speech is a nice-to-have */
  }
};

// ---- Sound -----------------------------------------------------------------
// A short synthesized "power-up" under the voice: sub drop, rising filtered
// sweep and a high shimmer, then a soft whoosh when the dots disperse.
let audioCtx;
const ctx = () => {
  const AC = window.AudioContext || window.webkitAudioContext;
  if (!AC) return null;
  audioCtx = audioCtx || new AC();
  if (audioCtx.state === "suspended") audioCtx.resume();
  return audioCtx;
};

export const playPowerUp = () => {
  const ac = ctx();
  if (!ac) return;
  const t = ac.currentTime;
  const master = ac.createGain();
  master.gain.value = 0.18;
  master.connect(ac.destination);

  const sub = ac.createOscillator();
  const subG = ac.createGain();
  sub.type = "sine";
  sub.frequency.setValueAtTime(110, t);
  sub.frequency.exponentialRampToValueAtTime(38, t + 0.9);
  subG.gain.setValueAtTime(0.9, t);
  subG.gain.exponentialRampToValueAtTime(0.001, t + 1.1);
  sub.connect(subG).connect(master);
  sub.start(t);
  sub.stop(t + 1.2);

  const saw = ac.createOscillator();
  const filt = ac.createBiquadFilter();
  const sawG = ac.createGain();
  saw.type = "sawtooth";
  saw.frequency.setValueAtTime(90, t);
  saw.frequency.exponentialRampToValueAtTime(420, t + 1.4);
  filt.type = "lowpass";
  filt.Q.value = 12;
  filt.frequency.setValueAtTime(300, t);
  filt.frequency.exponentialRampToValueAtTime(4200, t + 1.4);
  sawG.gain.setValueAtTime(0.0001, t);
  sawG.gain.exponentialRampToValueAtTime(0.35, t + 0.25);
  sawG.gain.exponentialRampToValueAtTime(0.001, t + 1.8);
  saw.connect(filt).connect(sawG).connect(master);
  saw.start(t);
  saw.stop(t + 1.9);

  [1320, 1760, 2640].forEach((f, i) => {
    const o = ac.createOscillator();
    const g = ac.createGain();
    o.type = "sine";
    o.frequency.value = f;
    const s = t + 1.2 + i * 0.07;
    g.gain.setValueAtTime(0.0001, s);
    g.gain.exponentialRampToValueAtTime(0.12, s + 0.02);
    g.gain.exponentialRampToValueAtTime(0.0001, s + 0.9);
    o.connect(g).connect(master);
    o.start(s);
    o.stop(s + 1);
  });
};

export const playWhoosh = () => {
  const ac = ctx();
  if (!ac) return;
  const t = ac.currentTime;
  const len = Math.floor(ac.sampleRate * 1.2);
  const buf = ac.createBuffer(1, len, ac.sampleRate);
  const data = buf.getChannelData(0);
  for (let i = 0; i < len; i++) data[i] = Math.random() * 2 - 1;
  const src = ac.createBufferSource();
  src.buffer = buf;
  const bp = ac.createBiquadFilter();
  bp.type = "bandpass";
  bp.Q.value = 1.2;
  bp.frequency.setValueAtTime(400, t);
  bp.frequency.exponentialRampToValueAtTime(3000, t + 1.1);
  const g = ac.createGain();
  g.gain.setValueAtTime(0.0001, t);
  g.gain.exponentialRampToValueAtTime(0.12, t + 0.3);
  g.gain.exponentialRampToValueAtTime(0.0001, t + 1.15);
  src.connect(bp).connect(g).connect(ac.destination);
  src.start(t);
};
